<template>
  <div
    v-click-outside="onClickOutside"
    :class="{
      dropdown: true,
      tooltip:
        !user?.role.permissions.pharmacy.update &&
        $route.name === 'EditPharmacy'
    }"
  >
    <div
      :class="{
        dropdown__head: true,
        'dropdown__head-disabled':
          !user?.role.permissions.pharmacy.update && isEditPharmacy,
        isOpened: isOpen,
        [className]: !!className
      }"
      @click="toggleDropdown"
    >
      <span v-if="label" class="dropdown__head-label">{{ label }}</span>
      <div class="dropdown__head-content">
        <img
          v-if="selectedOption?.img"
          :class="{
            'dropdown__head-content-icon': true,
            'dropdown__head-content-icon-box': $route.name !== 'Login'
          }"
          :src="selectedOption?.img"
          alt="Flag"
        />
        <input
          :class="{
            'dropdown__head-content-input': true,
            'dropdown__head-content-input-color':
              selectedOptionInput === $t('prevPeriod') ||
              selectedOptionInput === $t('average')
          }"
          type="text"
          :value="selectedOptionInput"
          :placeholder="placeholderText"
          :disabled="disabled"
          readonly
        />
      </div>
      <img
        :class="{
          'dropdown__head-img': true,
          'dropdown__head-img-disabled':
            !user?.role.permissions.pharmacy.update && isEditPharmacy
        }"
        src="@/assets/icons/Down.svg"
        alt=""
      />
    </div>
    <div v-if="isOpen && !disabled" class="dropdown__content">
      <div
        v-if="label === $t('serviceLanguagesAvailable')"
        class="dropdown__search"
      >
        <div class="input-block">
          <img
            class="dropdown-img"
            src="@/assets/icons/Search.svg"
            alt="Search"
          />
          <input
            v-model="search"
            class="input-search"
            :placeholder="$t('search')"
            @input="changeInput"
          />
        </div>
      </div>
      <div
        v-for="(option, idx) in localOptions"
        :key="option.value"
        :class="{
          'dropdown__content-option': true,
          disabled: option.disabled
        }"
        :data-testId="'dropdown' + idx"
        @click="selectOption(option)"
      >
        <div class="option__content">
          <img
            v-if="option.img"
            :class="{
              'option__content-icon': true,
              'option__content-icon-flag': $route.name === 'Login'
            }"
            :src="option?.img"
            alt="Flag"
          />
          <span
            :class="{
              'option__content-span': true,
              'option__content-span-color':
                option.value === 'prevPeriod' || option.value === 'average'
            }"
            >{{ option.label }}</span
          >
        </div>
        <Checkbox
          :value="selectedOptionCheckbox(option.value)"
          @change="selectOption(option)"
        />
      </div>
    </div>
    <span
      v-if="
        !user?.role.permissions.pharmacy.update &&
        $route.name === 'EditPharmacy'
      "
      class="tooltiptext"
    >
      {{ $t('enoughPermissions') }}
    </span>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapState } from 'vuex';

import vClickOutside from 'click-outside-vue3';

import { InputFileEvent, Option } from '@/types';

@Options({
  name: 'Dropdown',
  props: [
    'selectedOption',
    'className',
    'options',
    'label',
    'multiple',
    'iconRound',
    'placeholderText',
    'disabled'
  ],
  data() {
    return {
      isOpen: false,
      search: '',
      localOptions: [],
      allOptions: []
    };
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  created() {
    this.localOptions = this.options;
  },
  computed: {
    ...mapState('account', ['user']),
    isEditPharmacy() {
      return this.$route.name === 'EditPharmacy';
    },
    selectedOptionInput() {
      if (this.$route.name === 'Customers') {
        return this.getSelectedOptionInput('face', 'type');
      }

      if (this.multiple) {
        if (Array.isArray(this.selectedOption)) {
          return this.selectedOption.map((i: Option) => i.label).join(', ');
        } else if (this.selectedOption) {
          return this.selectedOption.label;
        } else {
          return '';
        }
      } else {
        return this.transformLabel(this.selectedOption?.label || '');
      }
    }
  },
  methods: {
    /* eslint-disable */
    selectOption(option: Option) {
      if (this.isOpen) {
        if (this.multiple) {
          const selectedValues = Array.isArray(this.selectedOption)
            ? this.selectedOption.map((i: Option) => i.value)
            : [];

          let values = Array.isArray(this.selectedOption)
            ? this.selectedOption
            : [];

          if (selectedValues.includes(option.value)) {
            values = values.filter((i: Option) => i.value !== option.value);
          } else {
            values = [...values, option];
          }

          this.$emit('selectOption', values);
        } else {
          this.$emit('selectOption', option);
          this.isOpen = false;
        }
      }
    },
    selectedOptionCheckbox(optionValue: Option) {
      if (this.multiple && this.isOpen) {
        return (
          Array.isArray(this.selectedOption) &&
          this.selectedOption.some((i: any) => i.value === optionValue)
        );
      }
      return this.selectedOption?.value === optionValue;
    },
    transformLabel(label: string) {
      const maxLength = this.$route.name === 'APIReservations' ? 30 : 20;

      return label.length > maxLength
        ? `${label.slice(0, maxLength)}...`
        : label;
    },
    toggleDropdown() {
      if (
        this.user?.role.permissions.pharmacy.update ||
        this.$route.name !== 'EditPharmacy'
      ) {
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
          this.loadOptions();
        } else {
          this.allOptions = [];
        }
      }
    },
    loadOptions() {
      const options = this.options;

      if (Array.isArray(options) && options.length > 0) {
        if (this.allOptions.length === 0) {
          this.allOptions = [...options];
          const chunkSize = 15;
          this.localOptions = options.slice(0, chunkSize);

          setTimeout(() => {
            this.localOptions = [
              ...this.localOptions,
              ...options.slice(chunkSize)
            ];
          }, 0);
        }
      }
    },
    onClickOutside() {
      this.isOpen = false;
      this.allOptions = [];

      this.clearSearch();
    },
    clearSearch() {
      this.search = '';
    },
    getSelectedOptionInput(faceLabel: string, typeLabel: string) {
      if (this.multiple && this.isOpen) {
        return this.selectedOption.length
          ? this.selectedOption.map((i: Option) => i.label).join(', ')
          : '';
      } else if (
        this.multiple &&
        !this.isOpen &&
        this.selectedOption.length &&
        !this.selectedOption.some(
          (option: Option) => option.label === this.$t(faceLabel)
        ) &&
        !this.selectedOption.some(
          (option: Option) => option.label === this.$t(typeLabel)
        )
      ) {
        return this.selectedOption.map((i: Option) => i.label).join(', ');
      }

      return this.transformLabel(this.selectedOption?.label || '');
    },
    changeInput(e: InputFileEvent) {
      const { value } = e.target;

      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        if (value === '') {
          this.localOptions = this.options;
        } else {
          this.localOptions = this.options.filter((option: Option) =>
            option.label.toLowerCase().includes(value.toLowerCase())
          );
        }
      }, 300);
    }
  },
  watch: {
    options() {
      if (this.options) {
        this.localOptions = this.options;
      }
    }
  }
})
export default class DropdownComponent extends Vue {}
</script>

<style lang="scss" scoped>
.dropdown {
  position: relative;
  display: inline-block;
  max-width: 413px;
  width: 100%;
  max-height: 46px;
  border-radius: 8px;
  .isOpened {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.tooltip {
  position: relative;
  .tooltiptext {
    position: absolute;
    top: -30%;
    left: 50%;
    transform: translate(-50%, -100%);
    visibility: hidden;
    width: 150px;
    font-size: $elevenSize;
    line-height: $elevenSize;
    background: $label;
    color: $white;
    text-align: center;
    border-radius: 6px;
    padding: 5px;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.3s;
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: $label transparent transparent transparent;
    }
  }

  &:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
  }
}

.dropdown__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  background-color: $white;
  border: 1px solid $border;
  border-radius: 8px;
  cursor: pointer;
  .dropdown__head-label {
    position: absolute;
    font-family: 'Ubuntu-Regular';
    top: -6px;
    left: 15px;
    padding: 0 5px;
    background-color: $white;
    color: $label;
    font-weight: 400;
    font-size: $twelveSize;
    line-height: $fourteenSize;
    z-index: 1;
  }
  .dropdown__head-content {
    display: flex;
    align-items: center;
    width: 100%;
    margin-left: 16px;
    border-radius: 8px;
    .dropdown__head-content-icon {
      width: 20px;
      height: 20px;
      margin-right: 8px;
    }
    .dropdown__head-content-icon-box {
      width: 14px;
      height: 12px;
      margin-right: 8px;
    }
    .dropdown__head-content-input {
      width: 100%;
      border: 0;
      font-size: $fourteenSize;
      outline: none;
      cursor: pointer;
      &.dropdown__head-content-input-color {
        color: $lineColor;
      }
    }
  }
  .dropdown__head-img {
    padding-right: 13px;
  }
  .dropdown__head-img-disabled {
    display: none;
  }
}

.dropdown__head-disabled {
  cursor: auto;
  .dropdown__head-content {
    .dropdown__head-content-input {
      cursor: auto;
    }
  }
}

.dropdown__content {
  position: absolute;
  max-width: 415px;
  width: 100%;
  max-height: 171px;
  border: 1px solid $border;
  border-top: 0;
  background-color: $white;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  z-index: 3;
  overflow-y: scroll;
  .dropdown__search {
    .input-block {
      position: relative;
      display: flex;
      align-items: center;
      height: 33px;
      padding: 12px 16px;
      .input-search {
        max-width: 330px;
        width: 100%;
        height: 38px;
        padding-left: 12px;
        font-size: $fourteenSize;
        border: 0;
        outline: none;
      }
    }
  }
  .dropdown__content-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 33px;
    padding: 12px 16px;
    cursor: pointer;
    .option__content {
      display: flex;
      align-items: center;
      font-size: $fourteenSize;
    }
    .option__content-span-color {
      color: $lineColor;
    }
    &:hover {
      background-color: $light-grey;
    }
    &:hover .option__content {
      background-color: $light-grey;
    }
    &:hover .option__content-span {
      background-color: $light-grey;
    }
  }
  .disabled {
    pointer-events: none;
    cursor: default;
    span {
      color: $label;
    }
  }
}

.option__content-icon {
  margin-right: 8px;
  max-width: 20px;
  max-height: 20px;
  &.option__content-icon-flag {
    border-radius: 9px;
  }
}
</style>
